import React from 'react';
import { ShieldCheck, FileSearch, GitBranch, Users, Activity } from 'lucide-react';
import { useReveal } from '@/lib/useReveal';

/**
 * OpportunityControls — institutional trust layer surfaced after Selected
 * Transactions and before Contact. Counterbalances the dynamic visual feel
 * with the actual controls applied to every opportunity before it reaches a
 * counterparty.
 *
 * Strictly factual copy — no fake diligence/compliance/certification claims,
 * no AI/predictive wording.
 */

const CONTROLS = [
  {
    icon: ShieldCheck,
    title: 'NDA-gated intake',
    body: 'Every submission lands behind an executed NDA before it surfaces to a counterparty.',
  },
  {
    icon: FileSearch,
    title: 'Document review',
    body: 'Sponsor materials, structure and supporting documents are reviewed and packaged before circulation.',
  },
  {
    icon: GitBranch,
    title: 'Mandate-fit distribution',
    body: 'Opportunities are routed only to counterparties whose mandate, region and ticket envelope match.',
  },
  {
    icon: Users,
    title: 'Role-based access',
    body: 'Internal, sponsor and capital-partner roles each see a scoped view of the deal room and Q&A.',
  },
  {
    icon: Activity,
    title: 'Audit-aware workflow',
    body: 'Document access, Q&A and interest events are recorded against each counterparty across the lifecycle.',
  },
];

export const OpportunityControls = () => {
  const [ref, visible] = useReveal({ threshold: 0.1 });

  return (
    <section
      ref={ref}
      className="gpc-section gpc-section--alt"
      data-testid="opportunity-controls-section"
    >
      <div className={`gpc-container gpc-reveal ${visible ? 'is-visible' : ''}`}>
        <div className="max-w-3xl mb-12">
          <p className="gpc-eyebrow mb-4">Controlled by design</p>
          <h2 className="gpc-h2 mb-4">
            How opportunities are <span style={{ color: 'var(--gpc-gold)', fontStyle: 'italic' }}>controlled</span> before distribution.
          </h2>
          <p className="gpc-body max-w-2xl">
            Every opportunity passes through the same controls before it reaches a capital partner — designed to keep distribution selective, confidential and accountable.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {CONTROLS.map((c) => (
            <div
              key={c.title}
              className="gpc-card flex flex-col gap-3"
              data-testid={`opportunity-control-${c.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
            >
              <div
                className="w-8 h-8 rounded-md flex items-center justify-center"
                style={{ background: 'rgba(201,168,106,0.10)' }}
              >
                <c.icon className="w-4 h-4" style={{ color: 'var(--gpc-gold)' }} />
              </div>
              <h3 className="gpc-h3">{c.title}</h3>
              <p className="gpc-body" style={{ fontSize: '14px', lineHeight: 1.6 }}>
                {c.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default OpportunityControls;
